import { type AirActionTrackingLocation } from '@air/analytics';
import { type Clip } from '@air/api/types';
import { clipCanOpenInCanvas } from '@air/feature-edit-canvas';

import { type UseOpenClipInCanvasReturn } from '~/hooks/canvases/useOpenClipInCanvas';
import { type EditInCanvasOpenedFrom } from '~/utils/canvases/getEditInCanvasAnalytics';

export type CropAndEditOnCanvasOpenedFrom = Extract<
  EditInCanvasOpenedFrom,
  | 'action_bar_export_as'
  | 'asset_modal_export_as'
  | 'gallery_item_export_as'
  | 'gallery_multi_select_export_as'
  | 'table_item_export_as'
  | 'table_multi_select_export_as'
>;

export type GetCropAndEditOnCanvasHandlerParams = {
  canCreateCanvas: boolean;
  clip: Clip;
  onBeforeOpen?: () => void;
  openClipInCanvas: UseOpenClipInCanvasReturn['openClipInCanvas'];
  openedFrom: CropAndEditOnCanvasOpenedFrom;
};

const BATCH_CROP_OPENED_FROM_BY_TRACK_LOCATION: Partial<
  Record<AirActionTrackingLocation, CropAndEditOnCanvasOpenedFrom>
> = {
  'action-bar': 'action_bar_export_as',
  'gallery-multi-select-context-menu': 'gallery_multi_select_export_as',
  'gallery-multi-select-ellipsis-menu': 'gallery_multi_select_export_as',
  'table-multi-select-context-menu': 'table_multi_select_export_as',
  'table-multi-select-ellipsis-menu': 'table_multi_select_export_as',
};

export const getBatchCropAndEditOnCanvasOpenedFrom = (
  trackLocation: AirActionTrackingLocation,
): CropAndEditOnCanvasOpenedFrom => BATCH_CROP_OPENED_FROM_BY_TRACK_LOCATION[trackLocation] ?? 'action_bar_export_as';

export type GetBatchCropAndEditOnCanvasHandlerParams = {
  canCreateCanvas: boolean;
  clips: Clip[];
  openClipInCanvas: UseOpenClipInCanvasReturn['openClipInCanvas'];
  openedFrom: CropAndEditOnCanvasOpenedFrom;
};

/**
 * Builds the multi-select Export As modal's "Crop and edit on Canvas" handler, or `undefined`
 * when none of the clips can open in a canvas from the current location so the modal hides the action.
 */
export const getBatchCropAndEditOnCanvasHandler = ({
  canCreateCanvas,
  clips,
  openClipInCanvas,
  openedFrom,
}: GetBatchCropAndEditOnCanvasHandlerParams) => {
  if (!canCreateCanvas || !clips.some((clip) => clipCanOpenInCanvas(clip))) {
    return undefined;
  }

  return () => {
    openClipInCanvas({
      action: 'crop',
      clips,
      openedFrom,
    });
  };
};

/**
 * Builds the Export As modal's "Crop and edit on Canvas" handler, or `undefined` when the clip
 * can't be opened in a canvas from the current location so the modal hides the action.
 */
export const getCropAndEditOnCanvasHandler = ({
  canCreateCanvas,
  clip,
  onBeforeOpen,
  openClipInCanvas,
  openedFrom,
}: GetCropAndEditOnCanvasHandlerParams) => {
  if (!canCreateCanvas || !clipCanOpenInCanvas(clip)) {
    return undefined;
  }

  return () => {
    onBeforeOpen?.();
    openClipInCanvas({
      action: 'crop',
      clips: [clip],
      openedFrom,
    });
  };
};
